/**
 * /audit-log — admin-only history viewer over the `audit_log` table.
 *
 * The DB trigger `trg_audit_fn` (db/sql/002_functions.sql) writes one
 * row here for every INSERT / UPDATE / DELETE on entity-scoped business
 * tables. This page is just a filtered, paginated read.
 *
 * Filters:
 *   table_name  — dropdown sourced from the API's distinct values
 *   action      — INSERT / UPDATE / DELETE / (any)
 *   user_id     — text input for now; switch to a user picker later
 *   row_id      — UUID input for "show me this one row's history"
 *   from / to   — date range
 *
 * Pagination: limit + offset; the API tells us if there's a next page.
 *
 * Each row expands to show old_data, new_data, changed_fields. For
 * UPDATEs, we render a compact JSON diff (only the changed keys).
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import {
  Button, Chip, MenuItem, Stack, TextField,
  Dialog, DialogContent, DialogTitle, DialogActions,
} from "@mui/material";

import { auditLogQueryOptions, meQueryOptions } from "@/lib/queries";
import { gridDefaults, FlexGrid } from "@/grids/gridDefaults";
import type {
  AuditAction, AuditLogFilters, AuditLogRow,
} from "@/lib/types";


export const Route = createFileRoute("/_auth/audit-log")({
  component: AuditLogPage,
});

const PAGE_SIZE = 100;

function AuditLogPage() {
  const navigate = useNavigate();
  const meQuery = useQuery(meQueryOptions());
  const isAdmin = meQuery.data?.role === "admin";

  // Inputs (uncommitted) so the user can tweak several fields and Apply once
  const [tableName, setTableName] = useState("");
  const [action, setAction] = useState<AuditAction | "">("");
  const [userId, setUserId] = useState("");
  const [rowId, setRowId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Committed filters — what's actually in flight
  const [filters, setFilters] = useState<AuditLogFilters>({
    limit: PAGE_SIZE, offset: 0,
  });

  const [detailRow, setDetailRow] = useState<AuditLogRow | null>(null);

  const auditQuery = useQuery({
    ...auditLogQueryOptions(filters),
    enabled: isAdmin,
  });

  const rows = auditQuery.data?.rows ?? [];
  const tableNames = auditQuery.data?.table_names ?? [];

  const applyFilters = () => {
    setFilters({
      table_name: tableName || undefined,
      action: action || undefined,
      user_id: userId.trim() || undefined,
      row_id: rowId.trim() || undefined,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
      limit: PAGE_SIZE,
      offset: 0,
    });
  };

  const clearFilters = () => {
    setTableName(""); setAction(""); setUserId(""); setRowId("");
    setFromDate(""); setToDate("");
    setFilters({ limit: PAGE_SIZE, offset: 0 });
  };

  const goPage = (newOffset: number) => {
    setFilters((f) => ({ ...f, offset: newOffset }));
  };

  const columns = useMemo<GridColDef<AuditLogRow>[]>(
    () => [
      {
        field: "at",
        headerName: "When",
        width: 170,
        valueFormatter: (v: string) => v ? new Date(v).toLocaleString() : "",
      },
      {
        field: "action",
        headerName: "Action",
        width: 100,
        renderCell: (p: GridRenderCellParams<AuditLogRow>) => (
          <Chip
            size="small"
            label={p.value as string}
            color={
              p.value === "INSERT" ? "success"
              : p.value === "UPDATE" ? "primary"
              : "error"
            }
            variant="outlined"
            sx={{ fontFamily: "monospace", fontSize: 11 }}
          />
        ),
      },
      {
        field: "table_name",
        headerName: "Table",
        width: 180,
        renderCell: (p) => (
          <span className="font-mono text-xs">{p.value as string}</span>
        ),
      },
      {
        field: "row_id",
        headerName: "Row",
        width: 280,
        renderCell: (p) =>
          p.value ? (
            <span className="font-mono text-[11px] text-slate-600 dark:text-slate-300">
              {(p.value as string).slice(0, 8)}…{(p.value as string).slice(-4)}
            </span>
          ) : (
            <span className="text-slate-400 dark:text-slate-500">—</span>
          ),
      },
      {
        field: "changed_fields",
        headerName: "Changed",
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (p) => {
          const fields = (p.value as string[] | null) ?? [];
          if (p.row.action === "INSERT") {
            return <span className="text-xs text-emerald-700">(new row)</span>;
          }
          if (p.row.action === "DELETE") {
            return <span className="text-xs text-red-700">(deleted)</span>;
          }
          if (fields.length === 0) {
            return <span className="text-xs text-slate-400 italic dark:text-slate-500">no field diff</span>;
          }
          return (
            <span className="text-xs">
              {fields.slice(0, 5).join(", ")}
              {fields.length > 5 && (
                <span className="text-slate-400 dark:text-slate-500"> +{fields.length - 5}</span>
              )}
            </span>
          );
        },
      },
      {
        field: "user_id",
        headerName: "User",
        width: 280,
        renderCell: (p) =>
          p.value ? (
            <span className="font-mono text-[11px] text-slate-600 dark:text-slate-300">
              {(p.value as string).slice(0, 8)}…{(p.value as string).slice(-4)}
            </span>
          ) : (
            <span className="text-xs text-slate-400 italic dark:text-slate-500">system</span>
          ),
      },
      {
        field: "actions",
        headerName: "",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Button
            size="small"
            onClick={() => setDetailRow(p.row)}
            sx={{ fontSize: 11 }}
          >
            View
          </Button>
        ),
      },
    ],
    [],
  );

  if (meQuery.isLoading) {
    return <div className="p-6 text-sm text-slate-500 dark:text-slate-400">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-800 max-w-2xl">
          The audit log viewer is restricted to administrators. The audit log
          itself is still being captured for every change — ask an admin to
          pull the records you need.
        </div>
        <Button
          sx={{ mt: 2 }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={() => navigate({ to: "/" as any })}
        >
          Back to dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Audit Log</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Every INSERT / UPDATE / DELETE on entity-scoped tables — captured by DB trigger
          </span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap" useFlexGap>
          <TextField
            label="Table"
            select
            size="small"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">(any)</MenuItem>
            {tableNames.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Action"
            select
            size="small"
            value={action}
            onChange={(e) => setAction(e.target.value as AuditAction | "")}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">(any)</MenuItem>
            <MenuItem value="INSERT">INSERT</MenuItem>
            <MenuItem value="UPDATE">UPDATE</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </TextField>
          <TextField
            label="User ID"
            size="small"
            placeholder="UUID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            label="Row ID"
            size="small"
            placeholder="UUID — show one row's history"
            value={rowId}
            onChange={(e) => setRowId(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <div style={{ flex: 1 }} />
          <Button variant="outlined" onClick={clearFilters} size="small">
            Clear
          </Button>
          <Button variant="contained" onClick={applyFilters} size="small">
            Apply
          </Button>
        </Stack>
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 px-6 py-4">
        <FlexGrid>
          <DataGridPro
            {...gridDefaults}
            rows={rows}
            columns={columns}
            loading={auditQuery.isFetching}
            getRowId={(r) => r.id}
            density="compact"
            pagination={false}
            hideFooter
            disableRowSelectionOnClick
          />
        </FlexGrid>
      </div>

      {/* Pagination footer */}
      <div className="bg-white border-t border-slate-200 px-6 py-2 flex items-center gap-3 dark:bg-gray-900 dark:border-gray-800">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {rows.length === 0
            ? "No rows match the current filters."
            : `Showing ${(filters.offset ?? 0) + 1}–${(filters.offset ?? 0) + rows.length}`}
        </span>
        <div className="flex-1" />
        <Button
          size="small"
          disabled={(filters.offset ?? 0) === 0}
          onClick={() => goPage(Math.max(0, (filters.offset ?? 0) - PAGE_SIZE))}
        >
          ← Prev
        </Button>
        <Button
          size="small"
          disabled={auditQuery.data?.next_offset == null}
          onClick={() => goPage(auditQuery.data!.next_offset!)}
        >
          Next →
        </Button>
      </div>

      <AuditDetailDialog row={detailRow} onClose={() => setDetailRow(null)} />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Detail dialog — old/new JSON, with a tiny "diff highlight" for UPDATE
// rows (only the changed_fields keys). Keeps the rest collapsed because
// audit rows can be large (every column gets included by trg_audit).
// ─────────────────────────────────────────────────────────────
function AuditDetailDialog({
  row, onClose,
}: { row: AuditLogRow | null; onClose: () => void }) {
  if (!row) return null;
  const changed = row.changed_fields ?? [];
  const isUpdate = row.action === "UPDATE";

  return (
    <Dialog open={Boolean(row)} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontSize: 15 }}>
        <span className="font-mono">{row.action}</span>{" "}
        on <span className="font-mono">{row.table_name}</span>
        <span className="ml-3 text-xs font-normal text-slate-500 dark:text-slate-400">
          {new Date(row.at).toLocaleString()}
        </span>
      </DialogTitle>
      <DialogContent dividers>
        <div className="grid grid-cols-3 gap-3 text-xs mb-3">
          <Field label="Row ID" value={row.row_id ?? "—"} mono />
          <Field label="User ID" value={row.user_id ?? "system"} mono />
          <Field label="Entity ID" value={row.entity_id ?? "—"} mono />
        </div>

        {isUpdate && changed.length > 0 && (
          <section className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1 dark:text-slate-300">
              Changed fields ({changed.length})
            </div>
            <div className="border border-slate-200 rounded overflow-hidden dark:border-gray-800">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 dark:bg-gray-950 dark:text-slate-400">
                  <tr>
                    <th className="text-left px-2 py-1 font-medium w-[20%]">Field</th>
                    <th className="text-left px-2 py-1 font-medium w-[40%]">Old</th>
                    <th className="text-left px-2 py-1 font-medium w-[40%]">New</th>
                  </tr>
                </thead>
                <tbody>
                  {changed.map((f) => (
                    <tr key={f} className="border-t border-slate-100 align-top dark:border-gray-800">
                      <td className="px-2 py-1 font-mono font-semibold text-slate-700 dark:text-slate-300">{f}</td>
                      <td className="px-2 py-1 font-mono text-red-700">
                        {formatVal(row.old_data?.[f])}
                      </td>
                      <td className="px-2 py-1 font-mono text-emerald-700">
                        {formatVal(row.new_data?.[f])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {row.old_data && (
          <section className="mb-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1 dark:text-slate-300">
              Old data
            </div>
            <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto max-h-64 dark:bg-gray-950 dark:border-gray-800">
              {JSON.stringify(row.old_data, null, 2)}
            </pre>
          </section>
        )}

        {row.new_data && (
          <section>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1 dark:text-slate-300">
              New data
            </div>
            <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto max-h-64 dark:bg-gray-950 dark:border-gray-800">
              {JSON.stringify(row.new_data, null, 2)}
            </pre>
          </section>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className={mono ? "font-mono text-[11px] text-slate-700 break-all" : "text-slate-700"}>
        {value}
      </div>
    </div>
  );
}

function formatVal(v: unknown): string {
  if (v === null || v === undefined) return "(null)";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}
